import { useEffect, useState, type FormEvent } from "react";
import type { Locale } from "@zoya/i18n";
import {
  createCustomerReturn,
  getCustomerReturns,
  getEligibleReturnOrders,
  type CustomerReturn,
  type EligibleReturnOrder,
  type ReturnReason,
} from "./api.js";

const copy = {
  en: {
    title: "Returns",
    intro:
      "Request a return for items delivered in the last 14 days. Our team reviews every request.",
    loadError: "Your returns could not be loaded.",
    startTitle: "Start a return",
    noneEligible: "No delivered orders are currently eligible for a return.",
    order: "Order",
    returnBy: "Return by",
    quantity: "Quantity to return",
    include: (title: string) => `Return ${title}`,
    reason: "Reason",
    preferred: "What would you like?",
    refund: "A refund",
    replacement: "A replacement",
    details: "Details",
    detailsHelp: "Required when the reason is Other.",
    submit: "Request return",
    submitting: "Sending…",
    chooseOne: "Choose at least one item.",
    errors: {
      RETURN_QUANTITY_EXCEEDED:
        "That quantity is no longer available to return.",
      RETURN_WINDOW_CLOSED: "The return window for this order has closed.",
      RETURN_NOT_ELIGIBLE: "This order is not eligible for a return.",
      RETURN_INPUT_INVALID: "Check the details and try again.",
      generic: "Your request could not be sent. Try again.",
    } as Record<string, string>,
    created: "Your return request was sent. We will update the status here.",
    yourReturns: "Your requests",
    none: "You have not requested any returns.",
    requested: "Requested",
    team: "Message from our team",
    status: {
      REQUESTED: "Waiting for review",
      APPROVED: "Approved — bring or send the items to us",
      REJECTED: "Not approved",
      RECEIVED: "Items received",
      RESOLVED: "Resolved",
    } as Record<string, string>,
    reasons: {
      DEFECTIVE: "Defective",
      DAMAGED_IN_TRANSIT: "Damaged in transit",
      NOT_AS_DESCRIBED: "Not as described",
      WRONG_ITEM: "Wrong item",
      CHANGED_MIND: "Changed my mind",
      OTHER: "Other",
    } as Record<ReturnReason, string>,
    resolutions: {
      REFUND_OWED: "A refund is owed to you and will be arranged by our team.",
      REPLACEMENT_ISSUED: "A replacement has been arranged.",
      NO_ACTION: "No further action is needed.",
    } as Record<string, string>,
  },
  ar: {
    title: "المرتجعات",
    intro:
      "اطلب إرجاع منتجات تم تسليمها خلال آخر 14 يومًا. يراجع فريقنا كل طلب.",
    loadError: "تعذر تحميل المرتجعات.",
    startTitle: "ابدأ طلب إرجاع",
    noneEligible: "لا توجد طلبات مسلّمة مؤهلة للإرجاع حاليًا.",
    order: "الطلب",
    returnBy: "آخر موعد للإرجاع",
    quantity: "الكمية المراد إرجاعها",
    include: (title: string) => `إرجاع ${title}`,
    reason: "السبب",
    preferred: "ماذا تفضّل؟",
    refund: "استرداد المبلغ",
    replacement: "استبدال المنتج",
    details: "التفاصيل",
    detailsHelp: "مطلوبة عندما يكون السبب «أخرى».",
    submit: "إرسال طلب الإرجاع",
    submitting: "جارٍ الإرسال…",
    chooseOne: "اختر منتجًا واحدًا على الأقل.",
    errors: {
      RETURN_QUANTITY_EXCEEDED: "هذه الكمية لم تعد متاحة للإرجاع.",
      RETURN_WINDOW_CLOSED: "انتهت مهلة الإرجاع لهذا الطلب.",
      RETURN_NOT_ELIGIBLE: "هذا الطلب غير مؤهل للإرجاع.",
      RETURN_INPUT_INVALID: "راجع البيانات وحاول مجددًا.",
      generic: "تعذر إرسال طلبك. حاول مرة أخرى.",
    } as Record<string, string>,
    created: "تم إرسال طلب الإرجاع. سنحدّث الحالة هنا.",
    yourReturns: "طلباتك",
    none: "لم تطلب أي إرجاع بعد.",
    requested: "تاريخ الطلب",
    team: "رسالة من فريقنا",
    status: {
      REQUESTED: "بانتظار المراجعة",
      APPROVED: "تمت الموافقة — أحضر المنتجات أو أرسلها إلينا",
      REJECTED: "لم تتم الموافقة",
      RECEIVED: "تم استلام المنتجات",
      RESOLVED: "تم الحل",
    } as Record<string, string>,
    reasons: {
      DEFECTIVE: "معيب",
      DAMAGED_IN_TRANSIT: "تالف أثناء الشحن",
      NOT_AS_DESCRIBED: "غير مطابق للوصف",
      WRONG_ITEM: "منتج خاطئ",
      CHANGED_MIND: "غيّرت رأيي",
      OTHER: "أخرى",
    } as Record<ReturnReason, string>,
    resolutions: {
      REFUND_OWED: "يستحق لك استرداد وسيرتّبه فريقنا.",
      REPLACEMENT_ISSUED: "تم ترتيب منتج بديل.",
      NO_ACTION: "لا حاجة لأي إجراء إضافي.",
    } as Record<string, string>,
  },
} as const;

function ReturnForm({
  order,
  locale,
  onCreated,
}: {
  order: EligibleReturnOrder;
  locale: Locale;
  onCreated: () => void;
}) {
  const t = copy[locale];
  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const [reason, setReason] = useState<ReturnReason>("DEFECTIVE");
  const [preferred, setPreferred] = useState<"REFUND" | "REPLACEMENT">(
    "REFUND",
  );
  const [details, setDetails] = useState("");
  const [key, setKey] = useState(() => crypto.randomUUID());
  const [status, setStatus] = useState<"idle" | "sending" | "error">("idle");
  const [error, setError] = useState("");
  const selected = order.items.filter(
    (item) => (quantities[item.orderItemPublicId] ?? 0) > 0,
  );

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (status === "sending") return;
    if (!selected.length) {
      setStatus("error");
      setError(t.chooseOne);
      return;
    }
    setStatus("sending");
    try {
      await createCustomerReturn(
        {
          orderPublicId: order.orderPublicId,
          items: selected.map((item) => ({
            orderItemPublicId: item.orderItemPublicId,
            quantity: quantities[item.orderItemPublicId]!,
          })),
          reason,
          details,
          preferredResolution: preferred,
        },
        key,
      );
      setKey(crypto.randomUUID());
      setQuantities({});
      setDetails("");
      setStatus("idle");
      onCreated();
    } catch (failure) {
      setStatus("error");
      setError(
        t.errors[failure instanceof Error ? failure.message : ""] ??
          t.errors.generic!,
      );
    }
  };

  return (
    <form
      className="engagement-form"
      onSubmit={(event) => void submit(event)}
      aria-label={`${t.order} ${order.orderNumber}`}
    >
      <h3>
        {t.order} {order.orderNumber}
      </h3>
      <p className="engagement-note">
        {t.returnBy}{" "}
        {new Date(order.deadline).toLocaleDateString(
          locale === "ar" ? "ar-EG" : "en-GB",
        )}
      </p>
      {order.items.map((item) => (
        <label key={item.orderItemPublicId}>
          <span>
            {t.include(item.title[locale])} — {t.quantity} (0–
            {item.returnableQuantity})
          </span>
          <input
            type="number"
            min={0}
            max={item.returnableQuantity}
            value={quantities[item.orderItemPublicId] ?? 0}
            onChange={(event) =>
              setQuantities((current) => ({
                ...current,
                [item.orderItemPublicId]: Math.max(
                  0,
                  Math.min(
                    item.returnableQuantity,
                    Math.trunc(Number(event.target.value) || 0),
                  ),
                ),
              }))
            }
          />
        </label>
      ))}
      <label>
        <span>{t.reason}</span>
        <select
          value={reason}
          onChange={(event) => setReason(event.target.value as ReturnReason)}
        >
          {Object.entries(t.reasons).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      </label>
      <fieldset>
        <legend>{t.preferred}</legend>
        <div className="engagement-rating-input">
          <label>
            <input
              type="radio"
              name={`preferred-${order.orderPublicId}`}
              checked={preferred === "REFUND"}
              onChange={() => setPreferred("REFUND")}
            />
            <span>{t.refund}</span>
          </label>
          <label>
            <input
              type="radio"
              name={`preferred-${order.orderPublicId}`}
              checked={preferred === "REPLACEMENT"}
              onChange={() => setPreferred("REPLACEMENT")}
            />
            <span>{t.replacement}</span>
          </label>
        </div>
      </fieldset>
      <label>
        <span>
          {t.details} ({t.detailsHelp})
        </span>
        <textarea
          rows={3}
          maxLength={1000}
          value={details}
          required={reason === "OTHER"}
          onChange={(event) => setDetails(event.target.value)}
        />
      </label>
      <div className="engagement-form__actions">
        <button
          className="button button--dark"
          type="submit"
          disabled={status === "sending"}
        >
          {status === "sending" ? t.submitting : t.submit}
        </button>
      </div>
      {status === "error" ? <p role="alert">{error}</p> : null}
    </form>
  );
}

export function CustomerReturns({ locale }: { locale: Locale }) {
  const t = copy[locale];
  const [attempt, setAttempt] = useState(0);
  const [created, setCreated] = useState(false);
  const [data, setData] = useState<{
    returns: CustomerReturn[];
    eligible: EligibleReturnOrder[];
  } | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let active = true;
    setFailed(false);
    Promise.all([getCustomerReturns(), getEligibleReturnOrders()])
      .then(([returns, eligible]) => active && setData({ returns, eligible }))
      .catch(() => active && setFailed(true));
    return () => {
      active = false;
    };
  }, [attempt]);
  return (
    <section aria-labelledby="returns-title" className="account-section">
      <h1 id="returns-title">{t.title}</h1>
      <p>{t.intro}</p>
      {failed ? <p role="alert">{t.loadError}</p> : null}
      <div role="status" aria-live="polite">
        {created ? <p>{t.created}</p> : null}
      </div>
      {data ? (
        <>
          <h2>{t.startTitle}</h2>
          {data.eligible.length ? (
            data.eligible.map((order) => (
              <ReturnForm
                key={order.orderPublicId}
                order={order}
                locale={locale}
                onCreated={() => {
                  setCreated(true);
                  setAttempt((value) => value + 1);
                }}
              />
            ))
          ) : (
            <p>{t.noneEligible}</p>
          )}
          <h2>{t.yourReturns}</h2>
          {data.returns.length ? (
            <ul className="engagement-review-list">
              {data.returns.map((entry) => (
                <li key={entry.returnPublicId}>
                  <strong>
                    {t.order} {entry.orderNumber}
                  </strong>
                  <span>{t.status[entry.status] ?? entry.status}</span>
                  <small>
                    {t.reasons[entry.reason]} · {t.requested}{" "}
                    {new Date(entry.requestedAt).toLocaleDateString(
                      locale === "ar" ? "ar-EG" : "en-GB",
                    )}
                  </small>
                  {entry.resolution ? (
                    <p>{t.resolutions[entry.resolution]}</p>
                  ) : null}
                  {entry.staffNote ? (
                    <p>
                      <em>{t.team}:</em> {entry.staffNote}
                    </p>
                  ) : null}
                </li>
              ))}
            </ul>
          ) : (
            <p>{t.none}</p>
          )}
        </>
      ) : failed ? null : (
        <p role="status">…</p>
      )}
    </section>
  );
}
